import { cn } from "@/lib/utils";
import { forwardRef, useEffect, useId, useRef, type HTMLAttributes, type ReactNode } from "react";
import { X } from "lucide-react";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  side?: "right" | "left";
}

export function Sheet({ open, onClose, title, description, children, footer, className, side = "right" }: SheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLElement>("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])")?.focus());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && dialogRef.current) {
        const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])")].filter((element) => !element.hasAttribute("disabled"));
        if (!focusable.length) return;
        const first = focusable[0]; const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); restoreFocusRef.current?.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex bg-slate-950/35 backdrop-blur-[2px]" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        ref={dialogRef}
        className={cn(
          "relative ml-auto flex h-full w-full max-w-md flex-col border-l bg-white shadow-lift",
          side === "left" && "ml-0 mr-auto border-r border-l-0",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b bg-white px-6 py-5">
          <div>
            {title && <h2 id={titleId} className="text-lg font-semibold tracking-tight">{title}</h2>}
            {description && <p id={descriptionId} className="mt-1 text-sm text-muted-foreground">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && <div className="border-t bg-muted/30 px-6 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export const FieldRow: React.FC<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("space-y-1.5", className)} {...props} />
);

export const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="text-xs text-destructive">{message}</p> : null;

export const Switch: React.FC<{
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
}> = ({ checked, onCheckedChange, disabled, id, ...rest }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    id={id}
    onClick={() => onCheckedChange(!checked)}
    className={cn(
      "inline-flex h-5 w-9 items-center rounded-full transition-colors",
      checked ? "bg-primary" : "bg-muted",
      disabled && "opacity-50 cursor-not-allowed",
    )}
    {...rest}
  >
    <span
      className={cn(
        "inline-block h-4 w-4 transform rounded-full bg-background shadow transition-transform",
        checked ? "translate-x-4" : "translate-x-0.5",
      )}
    />
  </button>
);

export const ConfirmDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: string;
  confirmLabel?: string;
  destructive?: boolean;
}> = ({ open, onClose, onConfirm, title, description, confirmLabel = "Confirm", destructive }) => (
  <Sheet open={open} onClose={onClose} side="right" className="max-w-sm">
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <button type="button" onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent">
          Cancel
        </button>
        <button
          type="button"
          onClick={async () => { await onConfirm(); onClose(); }}
          className={cn(
            "rounded-md px-3 py-1.5 text-sm text-primary-foreground",
            destructive ? "bg-destructive hover:bg-destructive/90" : "bg-primary hover:bg-primary/90",
          )}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </Sheet>
);

export const SectionHeading: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({ className, ...props }) => (
  <h3 className={cn("text-base font-semibold tracking-tight", className)} {...props} />
);

export const Muted: React.FC<HTMLAttributes<HTMLParagraphElement>> = ({ className, ...props }) => (
  <p className={cn("text-sm text-muted-foreground", className)} {...props} />
);

// Re-export forwardRef to satisfy older imports if any.
export { forwardRef };
